/**
 * Coming-soon landing page (public route /).
 *
 * Layout:
 *   - Full-bleed BirdScene (R3F) as the background
 *   - Frosted-glass overlay with the IntraAlpha wordmark, tagline, password
 *     gate, and a small "what's coming" feature reel
 *   - Footer with disclaimers
 *
 * Animations: Framer Motion for entry choreography, the BirdScene for ambient.
 */

import { motion } from "framer-motion";
import { Sparkles, Activity, Layers, ShieldCheck } from "lucide-react";
import { BirdScene } from "@/components/BirdScene";
import { PasswordGate } from "@/components/PasswordGate";
import logoUrl from "/brand/logo.png";

const VALUE_PILLARS = [
  {
    icon: Activity,
    title: "Real-time chains",
    body: "NBBO option chains, refreshed every 250 ms. Greeks computed client-side so the table stays interactive while the data flows.",
  },
  {
    icon: Layers,
    title: "Vol surface",
    body: "30 / 60-day historical vs implied, plotted across the surface. See where the premium ratio is statistically extreme.",
  },
  {
    icon: ShieldCheck,
    title: "Liquidity-first screener",
    body: "Filter by spread efficiency before premium. Skip the contracts you can't actually fill at the displayed price.",
  },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-ink-950">
      {/* Ambient WebGL background */}
      <div className="absolute inset-0 z-0">
        <BirdScene className="!h-full !w-full" intensity={1} />
      </div>

      {/* Vignette gradient over the canvas */}
      <div
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 30%, rgba(7,7,26,0.55) 65%, rgba(7,7,26,0.95) 100%)",
        }}
      />

      {/* Foreground content */}
      <div className="relative z-20 mx-auto flex max-w-7xl flex-col gap-24 px-6 pt-16 pb-24 lg:pt-24 lg:pb-32">
        {/* Top nav: just the wordmark, no menu (it's coming-soon) */}
        <motion.header
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between"
        >
          <img
            src={logoUrl}
            alt="IntraAlpha"
            className="h-8 w-auto opacity-90 mix-blend-screen"
            draggable={false}
          />
          <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-brand-500/40 bg-brand-500/10 px-3 py-1 text-xs font-medium text-brand-300">
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-brand-400" />
            </span>
            Launching Q3 2026
          </span>
        </motion.header>

        {/* Hero */}
        <section className="grid gap-16 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div className="flex flex-col gap-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="inline-flex w-fit items-center gap-2 rounded-full border border-ink-700 bg-ink-900/60 px-4 py-1.5 text-xs font-medium text-ink-300"
            >
              <Sparkles className="size-3.5 text-brand-400" />
              Options data, charting and tools — rebuilt
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-5xl font-bold leading-[1.05] tracking-tight text-ink-50 sm:text-6xl lg:text-7xl"
            >
              The options screener
              <br />
              you actually <span className="text-gradient-brand">trust</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-xl text-lg leading-relaxed text-ink-300"
            >
              Real-time chains. A liquidity-first screener. The volatility
              surface, plotted instead of guessed at. We rebuilt IntraAlpha
              from the data layer up — the new platform lands soon.
            </motion.p>

            <PasswordGate />
          </div>

          {/* Right: floating glass card with the bird logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:block"
          >
            <div className="glass relative aspect-square w-full max-w-md mx-auto rounded-[var(--radius-xl)] p-10 flex flex-col items-center justify-center gap-8">
              {/* Floating logo */}
              <motion.img
                src={logoUrl}
                alt=""
                aria-hidden
                draggable={false}
                className="w-3/4 h-auto mix-blend-screen"
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              {/* Mini metric badges */}
              <div className="grid grid-cols-3 gap-3 w-full">
                {[
                  { label: "tickers tracked", value: "8,400+" },
                  { label: "chains/sec", value: "250 ms" },
                  { label: "since", value: "2021" },
                ].map((m) => (
                  <div
                    key={m.label}
                    className="rounded-[var(--radius-md)] border border-ink-700/60 bg-ink-900/40 p-3 text-center"
                  >
                    <div className="font-display text-lg font-bold text-brand-300">
                      {m.value}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-400">
                      {m.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Glow blobs behind the card */}
            <div className="absolute inset-0 -z-10 blur-3xl">
              <div className="absolute left-1/2 top-1/2 size-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500/30" />
              <div className="absolute left-1/3 top-1/4 size-40 rounded-full bg-brand-400/20" />
            </div>
          </motion.div>
        </section>

        {/* Value pillars */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.1 } },
          }}
          className="grid gap-6 sm:grid-cols-3"
        >
          {VALUE_PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
                className="glass rounded-[var(--radius-lg)] p-6"
              >
                <div className="mb-4 inline-flex size-10 items-center justify-center rounded-[var(--radius-md)] bg-brand-500/15 text-brand-300">
                  <Icon className="size-5" />
                </div>
                <h3 className="font-display text-lg font-semibold text-ink-50">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-400">
                  {p.body}
                </p>
              </motion.div>
            );
          })}
        </motion.section>

        {/* Footer */}
        <footer className="mt-12 flex flex-col gap-2 border-t border-ink-700/40 pt-8 text-xs text-ink-500 sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} IntraAlpha. All rights reserved.</span>
          <span className="opacity-80">
            Options involve risk. Past performance is not indicative of future results.
          </span>
        </footer>
      </div>
    </div>
  );
}
